import { HTMLAttributes } from 'react';

import { useSelector } from 'hooks';

import { Interval, PlanUnitMix } from 'components/types';

import { createFilterOptions } from 'utils/filterOptions';

import { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';

import MuiAutocompleteOption from '../Autocomplete/MuiAutocompleteOption';

import { isValidPlanUnit } from './util';
import { Icon } from '@sdb/planning-ui';

export const useFilterOptions = () => {
  const { planUnitDisplayModeArr = [] } = useSelector(
    (state) => state.preferences
  );
  const showNameShort = planUnitDisplayModeArr.includes(2);
  const showCode = planUnitDisplayModeArr.includes(3);
  const keys = ['name'];

  if (showNameShort) {
    keys.push('nameShort');
  }

  if (showCode) {
    keys.push('code');
  }

  return createFilterOptions('name', keys);
};

export const useGetOptionLabel = () => {
  const preferences = useSelector((state) => state.preferences);
  const planUnitDisplayModeArr = preferences?.planUnitDisplayModeArr ?? [];

  const showNameShort = planUnitDisplayModeArr.includes(2);
  const showCode = planUnitDisplayModeArr.includes(3);

  return (option: PlanUnitMix | string) => {
    if (typeof option === 'string') {
      return option;
    }

    let label = option.name;
    if (showNameShort && 'nameShort' in option) {
      label += ` - ${option.nameShort}`;
    }
    if (showCode && 'code' in option) {
      label += ` - ${option.code}`;
    }
    return label;
  };
};

type RenderOptionProps = {
  isMulti?: boolean;
  compareInterval?: Interval;
};

export const useRenderOption = ({
  isMulti,
  compareInterval
}: RenderOptionProps = {}) => {
  const getOptionLabel = useGetOptionLabel();
  const { hasKidsPlan } = useSelector((state) => state.grants);

  return function (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: PlanUnitMix,
    { selected, inputValue }: AutocompleteRenderOptionState
  ) {
    // @ts-expect-error: bug in MUI, "key" is provided in props: HTMLAttributes<HTMLLIElement, but not typed
    props.key = option.uniqId || option.id;
    const isInvalid =
      compareInterval &&
      !('costCenters' in option) &&
      !isValidPlanUnit(option.validity, compareInterval);

    return (
      <MuiAutocompleteOption
        {...props}
        isMulti={isMulti}
        selected={selected}
        label={getOptionLabel(option)}
        isInvalid={isInvalid}
        inputValue={inputValue}
      >
        {option &&
          ('costCenters' in option || (hasKidsPlan && option.isKids)) && (
            <Icon
              type={'costCenters' in option ? 'folder' : 'children'}
              fontSize="small"
              className="left-icon"
            />
          )}
      </MuiAutocompleteOption>
    );
  };
};
